import React from 'react';
import { Editor } from 'react-draft-wysiwyg';
import EmojiPicker from 'emoji-picker-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBold, faItalic, faUnderline } from '@fortawesome/free-solid-svg-icons';
import { faFaceSmile } from '@fortawesome/free-regular-svg-icons';

/**
 * EditorField
 *
 * A self-contained rich-text editor with:
 *   – optional bold / italic / underline toolbar
 *   – character counter
 *   – emoji picker button
 *
 * Props come directly from the `useEditorField` hook return value,
 * plus a few component-specific ones.
 *
 * @prop {string}   label          - Field label text
 * @prop {boolean}  required       - Shows red asterisk
 * @prop {string}   hint           - Grey hint box text (e.g. "Max 60 characters")
 * @prop {boolean}  richText       - Show Bold/Italic/Underline toolbar (default true)
 * @prop {boolean}  disabled       - Disables everything (set to isSubmitting)
 * @prop {Object}   hook           - Return value of useEditorField()
 */
const EditorField = ({
    label    = 'Content',
    required = true,
    hint,
    richText = true,
    disabled = false,
    hook,
}) => {
    const {
        editorState,
        handleEditorStateChange,
        handleBeforeInput,
        handlePastedText,
        handleEmojiSelect,
        openEmojiPicker,
        showEmojiPicker,
        emojiPickerRef,
        editorRef,
        fieldErrors,
        charCount,
        charLimit,
        isOverLimit,
        toggleBold,
        toggleItalic,
        toggleUnderline,
        isBold,
        isItalic,
        isUnderline,
    } = hook;

    const toolbarOptions = { options: [] }; // hide Draft.js built-in toolbar

    return (
        <div className="mb-4 relative">
            <label className="block font-medium mb-2">
                {label}
                {required && <span className="text-red-500 pl-2 font-normal text-lg">*</span>}
            </label>

            {hint && (
                <div className="bg-gray-100 p-2 mb-2 rounded text-sm">
                    <p>{hint}</p>
                </div>
            )}

            <div
                className={`relative border rounded ${fieldErrors?.content ? 'border-red-500' : 'border-gray-300'}`}
                ref={editorRef}
            >
                {/* ── Rich-text toolbar ── */}
                {richText && (
                    <div className="border-b border-gray-300 p-2 bg-gray-50 flex gap-2">
                        {[
                            { icon: faBold,      active: isBold,      toggle: toggleBold,      title: 'Bold (Ctrl+B)'      },
                            { icon: faItalic,    active: isItalic,    toggle: toggleItalic,    title: 'Italic (Ctrl+I)'    },
                            { icon: faUnderline, active: isUnderline, toggle: toggleUnderline, title: 'Underline (Ctrl+U)' },
                        ].map(({ icon, active, toggle, title }) => (
                            <button
                                key={title}
                                type="button"
                                onClick={toggle}
                                disabled={disabled}
                                title={title}
                                className={`px-3 py-1 rounded transition-colors ${
                                    active
                                        ? 'bg-[#fa4b56] text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-400'
                                }`}
                            >
                                <FontAwesomeIcon icon={icon} />
                            </button>
                        ))}
                    </div>
                )}

                {/* ── Draft.js editor ── */}
                <Editor
                    editorState={editorState}
                    wrapperClassName="demo-wrapper"
                    editorClassName="demo-editor px-2 m-0 border-0 min-h-[70px]"
                    onEditorStateChange={handleEditorStateChange}
                    toolbar={toolbarOptions}
                    placeholder="Enter content"
                    readOnly={disabled}
                    handleBeforeInput={handleBeforeInput}
                    handlePastedText={handlePastedText}
                />

                {/* ── Footer: char count + emoji button ── */}
                <div className={`${richText ? 'border-t' : ''} border-gray-300 p-2 flex justify-between items-center`}>
                    <span className={`text-sm ${isOverLimit ? 'text-red-500' : 'text-gray-500'}`}>
                        {charCount}/{charLimit}
                    </span>
                    <button
                        type="button"
                        onClick={openEmojiPicker}
                        disabled={disabled}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <FontAwesomeIcon icon={faFaceSmile} size="lg" />
                    </button>
                </div>

                {/* ── Emoji picker ── */}
                {showEmojiPicker && (
                    <div
                        ref={emojiPickerRef}
                        className="absolute bottom-0 right-0 bg-white shadow-lg rounded-lg z-50"
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        <EmojiPicker
                            onEmojiClick={handleEmojiSelect}
                            width={300}
                            height={400}
                            previewConfig={{ showPreview: false }}
                            emojiStyle="native"
                            emojiVersion="12.0"
                        />
                    </div>
                )}
            </div>

            {fieldErrors?.content && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.content}</p>
            )}
        </div>
    );
};

export default EditorField;